import Vue from 'vue';
import Meta from 'vue-meta';
import VueCookies from 'vue-cookies';
import VueGtm from '@gtm-support/vue2-gtm';

import '@/core/style.css';
import '@/core/redesign.sass';
import store from '@/store';
import router from '@/core/router.js';
import i18n from '@/core/i18n.js';

import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

import { getAuthStateFromUrl } from '@/utilities/authState';

import App from './App.vue';

Vue.use(Meta);
Vue.use(VueCookies);
Vue.$cookies.config('','','',true);

Vue.use(i18n);

// Configure Google Tag Manager
if (process.env.GTM_ID) {
  Vue.use(VueGtm, {
    id: process.env.GTM_ID,
    enabled: true,
    debug: process.env.ENVIRONMENT === 'dev', // Enable debug mode only in dev environment
    loadScript: true, // Automatically load GTM script
    defer: true,
    trackOnNextTick: true,
  });
}

Vue.component('v-select', vSelect);

// Firebase is NOT loaded here anymore.
// Anonymous sign-in is handled lazily inside listenFirestore()
// in SignInVerifyEmail.vue and SignUpVerifyEmailStep.vue,
// so the 683KB Firebase SDK only loads when users reach the email verification screen.

// Delay Sentry until browser is idle so it doesn't compete with critical resources.
// requestIdleCallback fires when the browser has nothing urgent to do.
// timeout: 3000 ensures it loads within 3s even if the browser stays busy.
const loadSentry = () => {
  import('./utilities/sentry')
    .then(({ initSentry }) => {
      return initSentry({
        Vue,
        router,
        dsn: process.env.SENTRY_DSN,
      });
    })
    .then(() => console.log('Sentry init Success ~~~'))
    .catch(err => console.error('Sentry init Failed !!', err));
};

if ('requestIdleCallback' in window) {
  requestIdleCallback(loadSentry, { timeout: 3000 });
} else {
  setTimeout(loadSentry, 2000); // fallback for Safari
}

// === AUTO SOCIAL LOGIN - Navigate to prompt page or direct OAuth redirect ===
function handleAutoSocialLogin() {
  const urlParams = new URLSearchParams(window.location.search);
  const provider = urlParams.get('auto_social_login');

  if (!provider) return false;

  // Check if this is an OAuth callback (prevent infinite loop)
  const isOAuthCallback = urlParams.has('provider') || urlParams.has('error_code');
  if (isOAuthCallback) return false;

  // Get AuthState as unique identifier for this auth session
  const authState = urlParams.get('AuthState') || '';
  const storageKey = `auto_social_login_triggered_${authState}`;

  // Use localStorage instead of sessionStorage (persists across tabs/app switches on mobile)
  const existing = localStorage.getItem(storageKey);
  if (existing) {
    try {
      const data = JSON.parse(existing);
      if (data && data.ts && (Date.now() - data.ts <= 3600000)) return false;
      // Expired — remove and continue
      localStorage.removeItem(storageKey);
    } catch {
      return false; // Legacy plain-string format, treat as triggered
    }
  }

  // Build clean callback URL without auto_social_login parameter to prevent re-triggering
  const callbackUrl = new URL(window.location.href);
  callbackUrl.searchParams.delete('auto_social_login');
  const encodedCallback = encodeURIComponent(callbackUrl.toString());

  if (provider === 'line') {
    // LINE: auto-redirect to LINE OAuth (prompt page is now handled by MyAccount)
    localStorage.setItem(storageKey, JSON.stringify({ provider: 'line', ts: Date.now() }));
    const oauthUrl = `${location.origin}/signin/module.php/tmauth/linesigninauthrequest?event_page=sign_in&callback=${encodedCallback}&AuthState=${encodeURIComponent(getAuthStateFromUrl())}`;
    location.assign(oauthUrl);
    return true; // Prevent Vue app creation
  }

  if (provider === 'google' || provider === 'apple') {
    // Google/Apple: set localStorage and redirect directly to OAuth
    localStorage.setItem(storageKey, JSON.stringify({ provider, ts: Date.now() }));
    const authEndpoint = provider === 'google' ? 'googlesigninauthrequest' : 'applesigninauthrequest';
    const oauthUrl = `${location.origin}/signin/module.php/tmauth/${authEndpoint}?event_page=sign_in&callback=${encodedCallback}&AuthState=${encodeURIComponent(getAuthStateFromUrl())}`;
    location.assign(oauthUrl);
    return true; // Prevent Vue app creation
  }

  console.warn(`Unknown auto_social_login provider: ${provider}`);
  return false;
}

// Clean up expired auto_social_login keys (TTL: 1 hour)
function cleanupAutoSocialLoginKeys() {
  const TTL = 3600000;
  const now = Date.now();
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('auto_social_login_triggered_')) {
      try {
        const data = JSON.parse(localStorage.getItem(key));
        if (!data || !data.ts || (now - data.ts > TTL)) {
          keysToRemove.push(key);
        }
      } catch {
        keysToRemove.push(key); // Remove malformed entries too
      }
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
}
cleanupAutoSocialLoginKeys();

// Only create Vue app if no auto social login redirect
if (!handleAutoSocialLogin()) {
  new Vue({
    router,
    store,
    render: h => h(App),
  }).$mount('#root');
}
